import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/I18nContext";
import { bookDemo } from "@/lib/api";
import { CheckCircle2, Loader2, CalendarClock } from "lucide-react";

// Any CTA can open the demo modal by dispatching this window event.
export const openDemoModal = () => window.dispatchEvent(new CustomEvent("lp:book-demo"));

const COPY = {
  ro: {
    title: "Programează o demonstrație",
    subtitle: "Completează formularul și te contactăm în cel mai scurt timp.",
    name: "Nume", email: "Email", company: "Companie", phone: "Telefon",
    milk: "Volum lapte procesat", milkPh: "ex: 1000 L/zi", message: "Mesaj",
    messagePh: "Spune-ne câteva detalii despre fabrica ta…",
    submit: "Trimite cererea", sending: "Se trimite…",
    successTitle: "Îți mulțumim!", successMsg: "Am primit cererea ta. Te contactăm în curând.",
    close: "Închide", errName: "Numele este obligatoriu", errEmail: "Introdu un email valid",
    errorToast: "Ceva n-a mers. Te rugăm încearcă din nou.", optional: "opțional",
  },
  en: {
    title: "Book a demo",
    subtitle: "Fill in the form and we'll get back to you shortly.",
    name: "Name", email: "Email", company: "Company", phone: "Phone",
    milk: "Processed milk volume", milkPh: "e.g. 1000 L/day", message: "Message",
    messagePh: "Tell us a bit about your dairy…",
    submit: "Send request", sending: "Sending…",
    successTitle: "Thank you!", successMsg: "We've received your request and will be in touch soon.",
    close: "Close", errName: "Name is required", errEmail: "Enter a valid email",
    errorToast: "Something went wrong. Please try again.", optional: "optional",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DemoModal = () => {
  const { lang } = useT();
  const c = COPY[lang] || COPY.ro;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", milk_volume: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = () => { setDone(false); setErrors({}); setOpen(true); };
    window.addEventListener("lp:book-demo", handler);
    return () => window.removeEventListener("lp:book-demo", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = c.errName;
    if (!EMAIL_RE.test(form.email.trim())) errs.email = c.errEmail;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await bookDemo(form);
      setDone(true);
      setForm({ name: "", email: "", company: "", phone: "", milk_volume: "", message: "" });
    } catch {
      setErrors({ form: c.errorToast });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="lp-scope max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="demo-modal">
        {done ? (
          <div className="flex flex-col items-center py-8 text-center" data-testid="demo-success">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--lp-green)/0.12)]">
              <CheckCircle2 className="h-7 w-7 text-[hsl(var(--lp-green))]" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-[hsl(var(--lp-ink))]">{c.successTitle}</h3>
            <p className="mt-1.5 max-w-sm text-sm text-[hsl(var(--lp-body))]">{c.successMsg}</p>
            <Button className="mt-6" onClick={() => setOpen(false)} data-testid="demo-close-success">{c.close}</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-[hsl(var(--lp-green))]" /> {c.title}
              </DialogTitle>
              <DialogDescription>{c.subtitle}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="demo-name">{c.name} *</Label>
                <Input id="demo-name" value={form.name} onChange={set("name")} data-testid="demo-name"
                  aria-invalid={!!errors.name} />
                {errors.name && <p className="text-xs text-[hsl(var(--danger))]">{errors.name}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-email">{c.email} *</Label>
                <Input id="demo-email" type="email" value={form.email} onChange={set("email")} data-testid="demo-email"
                  aria-invalid={!!errors.email} />
                {errors.email && <p className="text-xs text-[hsl(var(--danger))]">{errors.email}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-company">{c.company} <span className="text-muted-foreground">({c.optional})</span></Label>
                <Input id="demo-company" value={form.company} onChange={set("company")} data-testid="demo-company" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-phone">{c.phone} <span className="text-muted-foreground">({c.optional})</span></Label>
                <Input id="demo-phone" value={form.phone} onChange={set("phone")} data-testid="demo-phone" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="demo-milk">{c.milk} <span className="text-muted-foreground">({c.optional})</span></Label>
                <Input id="demo-milk" placeholder={c.milkPh} value={form.milk_volume} onChange={set("milk_volume")} data-testid="demo-milk" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="demo-message">{c.message} <span className="text-muted-foreground">({c.optional})</span></Label>
                <Textarea id="demo-message" rows={3} placeholder={c.messagePh} value={form.message} onChange={set("message")} data-testid="demo-message" />
              </div>
            </div>

            {errors.form && <p className="text-sm text-[hsl(var(--danger))]">{errors.form}</p>}

            <Button
              className="mt-2 w-full bg-[hsl(var(--lp-navy))] text-white hover:bg-[hsl(var(--lp-navy)/0.92)]"
              onClick={submit} disabled={sending} data-testid="demo-submit"
            >
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {c.sending}</> : c.submit}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
